import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/common/StatusBadge';
import { formatDateTime } from '../../utils/date';
import * as api from '../../api/auditLogs';

function StatCard({ valor, label, cor }) {
  return (
    <div className="cjc-stat-card h-100">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: cor || 'var(--cjc-primary)' }}>{valor ?? '—'}</div>
    </div>
  );
}

function BarraHorizontal({ dados, campoLabel, max }) {
  if (!dados || dados.length === 0) {
    return <div className="text-muted p-3" style={{ fontSize: 13 }}>Sem dados no período.</div>;
  }
  const maxValor = max || Math.max(...dados.map(d => d.total), 1);
  return (
    <div className="p-3">
      {dados.map(d => (
        <div key={d[campoLabel]} className="mb-2">
          <div className="d-flex justify-content-between" style={{ fontSize: 13 }}>
            <span>{d[campoLabel]}</span>
            <span className="fw-semibold">{d.total}</span>
          </div>
          <div style={{ background: 'var(--cjc-gray-100)', borderRadius: 'var(--cjc-radius)', height: 8 }}>
            <div style={{
              width: `${(d.total / maxValor) * 100}%`, height: '100%',
              background: 'var(--cjc-primary)', borderRadius: 'var(--cjc-radius)',
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function BarraDiaria({ dados }) {
  if (!dados || dados.length === 0) {
    return <div className="text-muted p-3" style={{ fontSize: 13 }}>Sem atividade no período.</div>;
  }
  const max = Math.max(...dados.map(d => d.total), 1);
  return (
    <div className="d-flex align-items-end gap-1" style={{ height: 140, padding: '12px 8px 0', overflowX: 'auto' }}>
      {dados.map(d => (
        <div key={d.dia} className="d-flex flex-column align-items-center justify-content-end" style={{ minWidth: 10, height: '100%' }}
          title={`${d.dia}: ${d.total} evento(s)`}>
          <div style={{
            width: 8, minHeight: 2,
            height: `${Math.max((d.total / max) * 100, 2)}%`,
            background: 'var(--cjc-accent)',
            borderRadius: '2px 2px 0 0',
          }} />
        </div>
      ))}
    </div>
  );
}

const PERIODOS = [
  { value: 7, label: 'Últimos 7 dias' },
  { value: 30, label: 'Últimos 30 dias' },
  { value: 90, label: 'Últimos 90 dias' },
];

export default function PainelAuditoria() {
  const [dias, setDias] = useState(30);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(() => {
    setLoading(true);
    setErro('');
    api.getResumo(dias)
      .then(r => setDados(r.data))
      .catch(() => setErro('Erro ao carregar o painel de auditoria. Tente novamente.'))
      .finally(() => setLoading(false));
  }, [dias]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div>
      <PageHeader titulo="Painel de Auditoria" subtitulo="Resumo de acessos e cadastros — baseado no log de auditoria">
        <select className="form-select form-select-sm" style={{ width: 180 }} value={dias} onChange={e => setDias(Number(e.target.value))}>
          {PERIODOS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </PageHeader>

      {erro && <div className="alert alert-danger">{erro}</div>}
      {loading && <LoadingSpinner />}

      {!loading && dados && (
        <>
          <div className="row g-3">
            <div className="col-6 col-lg-3"><StatCard valor={dados.total_eventos} label="Eventos no Período" /></div>
            <div className="col-6 col-lg-3"><StatCard valor={dados.total_acessos} label="Acessos (Logins)" cor="var(--cjc-accent)" /></div>
            <div className="col-6 col-lg-3"><StatCard valor={dados.total_cadastros} label="Novos Cadastros" cor="#1a7a3d" /></div>
            <div className="col-6 col-lg-3"><StatCard valor={dados.usuarios_mais_ativos.length} label="Usuários com Atividade" /></div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-12">
              <div className="card">
                <div className="card-header">Atividade por Dia</div>
                <div className="card-body">
                  <BarraDiaria dados={dados.por_dia} />
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-lg-6">
              <div className="card h-100">
                <div className="card-header">Por Ação</div>
                <BarraHorizontal dados={dados.por_acao} campoLabel="acao" />
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card h-100">
                <div className="card-header">Por Entidade</div>
                <BarraHorizontal dados={dados.por_entidade} campoLabel="entidade" />
              </div>
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-lg-5">
              <div className="card h-100">
                <div className="card-header">Usuários Mais Ativos</div>
                <div className="card-body p-0">
                  <table className="table table-hover mb-0">
                    <thead><tr><th>Usuário</th><th>Perfil</th><th>Eventos</th></tr></thead>
                    <tbody>
                      {dados.usuarios_mais_ativos.length === 0 && (
                        <tr><td colSpan={3} className="text-center text-muted py-3">Nenhuma atividade no período.</td></tr>
                      )}
                      {dados.usuarios_mais_ativos.map(u => (
                        <tr key={u.email}>
                          <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</td>
                          <td>{u.perfil ? <StatusBadge status={u.perfil} /> : '—'}</td>
                          <td>{u.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-lg-7">
              <div className="card h-100">
                <div className="card-header">Atividade Recente</div>
                <div className="card-body p-0">
                  <table className="table table-hover mb-0">
                    <thead><tr><th>Quando</th><th>Usuário</th><th>Ação</th><th>Entidade</th></tr></thead>
                    <tbody>
                      {dados.atividade_recente.length === 0 && (
                        <tr><td colSpan={4} className="text-center text-muted py-3">Nenhuma atividade registrada.</td></tr>
                      )}
                      {dados.atividade_recente.map((a, i) => (
                        <tr key={i}>
                          <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(a.criado_em)}</td>
                          <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.email || '—'}</td>
                          <td><span className="badge bg-secondary">{a.acao}</span></td>
                          <td>{a.entidade}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-footer text-end">
                  <Link to="/logs" className="btn btn-sm btn-outline-primary">Ver todos os logs →</Link>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
